/**
 * Presentation-only heuristics for Option B (shift-graph next question).
 * Cumulative levels 1–7: higher iteration enables stricter suppression rules.
 * Does not use oracle labels (e.g. intended diagnosis) — safe for production.
 *
 * @module optionBPresentationHeuristics
 */

/** Default when callers want “all rules” (ChartMind V2, full A/B). */
export const OPTION_B_PRESENTATION_RULE_ITERATION_MAX = 7;

/**
 * @param {unknown} raw
 * @returns {number} clamped 0–7; 0 disables extra heuristics beyond callers’ other filters.
 */
export function normalizeOptionBPresentationRuleIteration(raw) {
  if (raw === undefined || raw === null || raw === "") {
    return OPTION_B_PRESENTATION_RULE_ITERATION_MAX;
  }
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n)) return OPTION_B_PRESENTATION_RULE_ITERATION_MAX;
  return Math.max(0, Math.min(OPTION_B_PRESENTATION_RULE_ITERATION_MAX, n));
}

/**
 * @typedef {Object} OptionBPresentationRuleContext
 * @property {string} transcriptLower
 * @property {string} chiefComplaintLower
 * @property {string} presentationBlobLower — CC + extras + synthetic age/sex hints
 * @property {number | null} ageYears
 * @property {'male' | 'female' | null} sex
 * @property {boolean} ageMentionedInText
 */

/**
 * @param {object} p
 * @param {string} [p.chiefComplaint]
 * @param {string[]} [p.additionalLines]
 * @param {number | null | undefined} p.ageYears
 * @param {'male' | 'female' | null | undefined} p.sex
 * @returns {OptionBPresentationRuleContext}
 */
export function buildPresentationRuleContextFromNarrative(p) {
  const chief = String(p.chiefComplaint || "")
    .trim()
    .toLowerCase();
  const add = (Array.isArray(p.additionalLines) ? p.additionalLines : [])
    .map((s) => String(s || "").trim().toLowerCase())
    .filter(Boolean)
    .join("\n");
  const transcript = [`chief complaint: ${chief}`, add].filter(Boolean).join("\n");
  const ageYears =
    typeof p.ageYears === "number" && Number.isFinite(p.ageYears) ? p.ageYears : null;
  const sex =
    p.sex === "male" || p.sex === "female"
      ? /** @type {'male' | 'female'} */ (p.sex)
      : null;
  const ageHint = ageYears != null ? `${ageYears}-year-old` : "";
  const sexHint = sex || "";
  const presentationBlobLower = [chief, add, ageHint, sexHint].join(" ").toLowerCase();
  const ageMentionedInText =
    /\b\d{1,3}\s*[-]?\s*(year|years|yo|y\.o\.)\b/i.test(transcript) ||
    /\bpatient:\s*\d{1,3}\b/i.test(transcript) ||
    ageYears != null;
  return {
    transcriptLower: transcript,
    chiefComplaintLower: chief,
    presentationBlobLower,
    ageYears,
    sex,
    ageMentionedInText,
  };
}

/** Chief-complaint tokens → label patterns: suppress factor if anchor hits CC and label matches. */
const CHIEF_ANCHOR_SUPPRESSIONS = [
  { tokens: ["fever", "febrile", "pyrex"], labelRe: /(fever|febrile|pyrex|temperature\s*(elev|high)?)/i },
  { tokens: ["chest pain", "substernal", "precordial"], labelRe: /(chest\s*pain|substernal|precordial)/i },
  { tokens: ["headache", "cephal"], labelRe: /(headache|cephal)/i },
  {
    tokens: ["shortness of breath", "dyspnea", "sob", "breathless"],
    labelRe: /(shortness|dyspnea|\bsob\b|hypox|oxygen|\bo2\b|spo2|ra\s*room\s*air)/i,
  },
  {
    tokens: ["weakness", "numbness", "slurred", "dysarthria"],
    labelRe: /(weakness|numbness|slur|dysarthria|hemipares|focal\s*deficit)/i,
  },
  {
    tokens: ["abdominal pain", "belly pain", "stomach pain"],
    labelRe: /(abdominal|epigastric|\bruq\b|\brlq\b|\bllq\b|\bluq\b|periumbilical)/i,
  },
  { tokens: ["calf", "leg swelling", "edema leg"], labelRe: /(calf|leg\s*swelling|unilateral\s*leg|dvt)/i },
  {
    tokens: ["neck stiff", "stiff neck", "mening", "photophobia"],
    labelRe: /(neck\s*stiff|stiff\s*neck|mening|kernig|photophobia|brudzinski)/i,
  },
  { tokens: ["nausea", "vomiting", "emesis"], labelRe: /(nausea|vomiting|emesis)/i },
  { tokens: ["sore throat", "pharyng"], labelRe: /(sore\s*throat|pharyng|tonsillar)/i },
  { tokens: ["back pain", "radicul", "sciatica"], labelRe: /(back\s*pain|radicul|sciatica|lumbar)/i },
];

const AGE_NOISE_IN_LABEL_RE =
  /\b(age\s*([><≥≤=]|greater\s+than|less\s+than|over|under)|>\s*65|≥\s*65|<\s*50|under\s*50|over\s*65|elderly|geriatric|neonate|infant|toddler|adolescent|pediatric|young\s*adult|middle[\s-]*age|non-?pregnant|childbearing|gravida|parity|lmp|last\s*menstrual)\b/i;

const VITALS_IN_TEXT_RE =
  /\b(o2|spo2|oxygen|sa?t\s*\d{2,3}|bp|blood\s*pressure|heart\s*rate|\bhr\b|pulse|temp(erature)?)\b/i;

const VITALS_IN_LABEL_RE =
  /\b(o2|spo2|oxygen|hypox|sa?t|blood\s*pressure|\bbp\b|heart\s*rate|\bhr\b|pulse|temp(erature)?|febrile)\b/i;

/**
 * @param {{ factorId?: string, label?: string | null, category?: string | null }} factor
 * @param {OptionBPresentationRuleContext} ctx
 * @param {number} ruleIteration 0–7 cumulative
 * @returns {boolean} true → drop this factor from next-question candidacy
 */
export function shouldSuppressShiftFactorForPresentation(factor, ctx, ruleIteration) {
  if (ruleIteration < 1) return false;
  const label = String(factor.label || "").trim();
  const L = label.toLowerCase();
  const cat = String(factor.category || "").trim().toLowerCase();
  const blob = ctx.presentationBlobLower || "";
  const chief = ctx.chiefComplaintLower || "";

  if (ruleIteration >= 1) {
    if (
      ctx.ageYears != null &&
      ctx.ageMentionedInText &&
      cat === "patient_context" &&
      AGE_NOISE_IN_LABEL_RE.test(L)
    ) {
      return true;
    }
  }

  if (ruleIteration >= 2) {
    if (ctx.sex === "male") {
      if (
        /\b(female|pregnant|pregnancy|gravida|parity|lmp|menstrual|gynec|ectopic|pre-?eclampsia)\b/i.test(
          L
        )
      ) {
        return true;
      }
    }
    if (ctx.sex === "female") {
      if (/\b(male\s+sex|sex\s*:\s*male|biological\s+male)\b/i.test(L)) {
        return true;
      }
    }
  }

  if (ruleIteration >= 3) {
    for (const row of CHIEF_ANCHOR_SUPPRESSIONS) {
      const hitChief = row.tokens.some((t) => chief.includes(t));
      if (!hitChief) continue;
      if (row.labelRe.test(L)) return true;
    }
  }

  if (ruleIteration >= 4) {
    if (VITALS_IN_TEXT_RE.test(blob) && VITALS_IN_LABEL_RE.test(L)) {
      return true;
    }
  }

  if (ruleIteration >= 5) {
    const m = L.match(/\bhistory\s+of\s+([a-z]{4,})\b/i);
    if (m && m[1] && blob.includes(String(m[1]).toLowerCase())) {
      return true;
    }
  }

  if (ruleIteration >= 6) {
    if (/\b(any\s+recent|have\s+you\s+had|does\s+the\s+patient\s+have)\b/i.test(L)) {
      const stripped = L.replace(/[^a-z0-9\s]/gi, " ");
      const words = stripped
        .split(/\s+/)
        .map((w) => w.trim())
        .filter((w) => w.length >= 5 && !GENERIC_STOPWORDS.has(w));
      const hits = words.filter((w) => blob.includes(w));
      if (words.length >= 2 && hits.length >= Math.min(2, words.length)) {
        return true;
      }
    }
  }

  if (ruleIteration >= 7) {
    if (
      ctx.ageYears != null &&
      cat === "patient_context" &&
      label.length > 0 &&
      label.length <= 36 &&
      /\b(age|sex|gender|pregn|gravida|parity|menstrual|lmp|pediatric|geriatric|elderly|adult|child)\b/i.test(
        L
      )
    ) {
      return true;
    }
  }

  return false;
}

const GENERIC_STOPWORDS = new Set([
  "patient",
  "history",
  "present",
  "recent",
  "clinical",
  "symptom",
  "symptoms",
  "factor",
  "positive",
  "negative",
]);

/**
 * Filter factor ids; if the filter would remove everything, returns the original list.
 *
 * @param {string[]} factorIds
 * @param {(id: string) => { label?: string | null, category?: string | null }} getMeta
 * @param {OptionBPresentationRuleContext} ctx
 * @param {number} ruleIteration
 * @returns {string[]}
 */
export function filterFactorIdsWithPresentationHeuristics(factorIds, getMeta, ctx, ruleIteration) {
  const ids = [...new Set((factorIds || []).filter(Boolean).map(String))];
  if (ruleIteration < 1 || !ids.length) return ids;
  const kept = ids.filter(
    (id) => !shouldSuppressShiftFactorForPresentation({ factorId: id, ...getMeta(id) }, ctx, ruleIteration)
  );
  return kept.length > 0 ? kept : ids;
}
